import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { RootState, setProfile, setQuestion, setSelectedCertificationCli, setTimeRemaining } from "../../store";
import { baseUrl } from "../../constants/api";
import { API_ENDPOINTS } from "../../constants/endpoints";
import showToastMessage from "../../utils/showToastMessage";
import { FullScreenContext } from "../../App";

type AssignedCli = {
  cli_id: string;
  name: string;
  reference_id?: string;
};

const CertificationCliSelect = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { handle } = useContext(FullScreenContext);
  const { token, profile } = useSelector((state: RootState) => state.account);
  const { quizzes, selectedCertificationCli } = useSelector((state: RootState) => state.quiz);
  const [assignedCli, setAssignedCli] = useState<AssignedCli | null>(
    profile?.cli_id
      ? {
        cli_id: String(profile.cli_id),
        name: profile.cli_name || "Your assigned CLI",
        reference_id: profile.reference_id,
      }
      : null
  );
  const [durationMonths, setDurationMonths] = useState<number>(
    selectedCertificationCli && selectedCertificationCli.quiz_id === id
      ? selectedCertificationCli.duration_months || 1
      : 1
  );
  const [loading, setLoading] = useState(false);

  const selectedQuiz = useMemo(
    () => (quizzes || []).find((quiz: any) => quiz.quiz_id === id),
    [quizzes, id]
  );
  const existingSelection = selectedQuiz?.certification_selection;

  useEffect(() => {
    if (!selectedQuiz) {
      navigate("/", { replace: true });
      return;
    }

    if (selectedQuiz.quiz_type !== "certification") {
      navigate(`/quiz/${id}/start`, { replace: true });
    }
  }, [selectedQuiz, navigate, id]);

  useEffect(() => {
    if (existingSelection) {
      setDurationMonths(Number(existingSelection.duration_months) || 1);
    }
  }, [existingSelection]);

  useEffect(() => {
    if (profile?.cli_id) {
      setAssignedCli({
        cli_id: String(profile.cli_id),
        name: profile.cli_name || "Your assigned CLI",
        reference_id: profile.reference_id,
      });
      return;
    }

    if (!token) {
      return;
    }

    let isActive = true;
    const fetchAssignedCli = async () => {
      try {
        const response = await fetch(`${baseUrl}${API_ENDPOINTS.GET_USER_DETAILS}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || "Failed to fetch your assigned CLI");
        }
        if (!data.data?.cli_id) {
          throw new Error("No CLI is assigned to your account. Please contact your administrator.");
        }
        if (isActive) {
          dispatch(setProfile(data.data));
          setAssignedCli({
            cli_id: String(data.data.cli_id),
            name: data.data.cli_name || "Your assigned CLI",
            reference_id: data.data.reference_id,
          });
        }
      } catch (error) {
        if (isActive) {
          showToastMessage(
            error instanceof Error ? error.message : "Unable to load your assigned CLI.",
            "error"
          );
        }
      }
    };

    fetchAssignedCli();
    return () => {
      isActive = false;
    };
  }, [token, profile?.cli_id, profile?.cli_name, profile?.reference_id, dispatch]);

  const fetchQuestions = async () => {
    if (!selectedQuiz || !id) {
      return;
    }

    const response = await fetch(`${baseUrl}${API_ENDPOINTS.GET_QUESTIONS_BY_QUIZ}/${id}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to fetch quiz questions");
    }

    const questionData = await response.json();
    dispatch(setQuestion({
      questions: questionData.questions,
      quiz_duration: selectedQuiz.quiz_duration,
    }));
    dispatch(setTimeRemaining(parseInt(selectedQuiz.quiz_duration) * 60));
  };

  const handleContinue = async () => {
    if (!assignedCli || !id) {
      showToastMessage("No CLI is assigned to your account. Please contact your administrator.", "error");
      return;
    }
    if (!Number.isInteger(durationMonths) || durationMonths < 1 || durationMonths > 12) {
      showToastMessage("Please select a duration between 1 and 12 months.", "error");
      return;
    }

    setLoading(true);
    try {
      const lockResponse = await fetch(`${baseUrl}${API_ENDPOINTS.LOCK_CERTIFICATION_SELECTION}/${id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          selected_certificate_cli_id: assignedCli.cli_id,
          duration_months: durationMonths,
        }),
      });
      const lockData = await lockResponse.json();
      if (!lockResponse.ok) {
        throw new Error(lockData?.message || "Failed to lock certification selection");
      }
      const lockedSelection = lockData.selection || {};
      await fetchQuestions();
      dispatch(setSelectedCertificationCli({
        quiz_id: id,
        cli_id: assignedCli.cli_id,
        cli_name: lockedSelection.cli_name || assignedCli.name,
        reference_id: assignedCli.reference_id || "",
        duration_months: lockedSelection.duration_months || durationMonths,
        due_date: lockedSelection.due_date,
      }));

      navigate(`/quiz/${id}/start`);
      if (handle) {
        handle.enter();
      }
    } catch (error) {
      showToastMessage(
        error instanceof Error ? error.message : "Failed to prepare certification quiz. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!selectedQuiz) {
    return null;
  }

  return (
    <div className="mx-auto flex min-h-[70vh] w-full max-w-4xl flex-col justify-center px-6 py-10">
      <div className="rounded-[28px] border border-[#e7e7e7] bg-white p-8 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
        <div className="mb-8">
          <p className="mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-[#6B7280]">Certification Quiz</p>
          <h1 className="text-3xl font-semibold text-[#111827]">{selectedQuiz.quiz_name}</h1>
          <p className="mt-3 max-w-2xl text-sm text-[#6B7280]">
            Your signup-assigned CLI will be used for this certification quiz. Choose the certificate validity duration before continuing.
          </p>
        </div>

        <div className="rounded-[24px] border border-[#ececec] bg-[#fafafa] p-6">
          <label className="mb-3 block text-sm font-semibold text-[#111827]">Assigned CLI</label>
          <div className="flex min-h-14 items-center rounded-2xl border-[1.5px] border-[#d7dbe7] bg-white px-4 text-sm text-[#111827]">
            {assignedCli
              ? `${assignedCli.name}${assignedCli.reference_id ? ` (${assignedCli.reference_id})` : ""}`
              : "Loading your assigned CLI..."}
          </div>

          <div className="mt-5">
            <label className="mb-3 block text-sm font-semibold text-[#111827]">Duration</label>
            <select
              className="h-14 w-full rounded-2xl border-[1.5px] border-[#d7dbe7] bg-white px-4 text-sm text-[#111827]"
              value={durationMonths}
              onChange={(event) => setDurationMonths(Number(event.target.value))}
              disabled={Boolean(existingSelection)}
            >
              {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
                <option key={month} value={month}>
                  {month} {month === 1 ? "month" : "months"}
                </option>
              ))}
            </select>
          </div>

          {assignedCli && (
            <div className="mt-5 rounded-2xl border border-[#d9e7d7] bg-[#f3fbf2] p-4">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#4B5563]">
                Signup-assigned CLI
              </div>
              <div className="mt-2 text-lg font-semibold text-[#111827]">{assignedCli.name}</div>
              {assignedCli.reference_id && (
                <div className="text-sm text-[#4B5563]">HRMS {assignedCli.reference_id}</div>
              )}
              <div className="mt-2 text-sm text-[#4B5563]">
                Duration: {durationMonths} {durationMonths === 1 ? "month" : "months"}
                {existingSelection?.due_date
                  ? ` • Due Date: ${new Date(existingSelection.due_date).toLocaleDateString("en-GB")}`
                  : ""}
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate("/")}>
            Cancel
          </Button>
          <Button type="button" onClick={handleContinue} disabled={loading || !assignedCli}>
            {loading ? "Preparing Quiz..." : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CertificationCliSelect;
